import { useMemo } from 'react';

import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import type { USER_LANGUAGE_SETTING, USER_STATUS_COLOR_MODE } from 'cpool-constants';
import { USER_LANGUAGE_SETTINGS, USER_STATUS_COLOR_MODES } from 'cpool-constants';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormRow } from '~/components/FormRow/FormRow';
import { SectionCard } from '~/components/SectionCard/SectionCard';

type DisplayForm = { language: USER_LANGUAGE_SETTING; statusColorMode: USER_STATUS_COLOR_MODE };

export const DisplaySettings: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
    const { t } = useTranslation();
    const { field: languageField } = useController<DisplayForm, 'language'>({ name: 'language' });
    const { field: statusColorModeField } = useController<DisplayForm, 'statusColorMode'>({
        name: 'statusColorMode',
    });

    const languageOptions = useMemo(
        () => [
            { label: t('TOP_HEADER.USER_SETTINGS.SETTINGS.DISPLAY.LANGUAGE_AUTO'), value: USER_LANGUAGE_SETTINGS.auto },
            { label: t('TOP_HEADER.USER_SETTINGS.SETTINGS.DISPLAY.LANGUAGE_DE'), value: USER_LANGUAGE_SETTINGS.de },
            { label: t('TOP_HEADER.USER_SETTINGS.SETTINGS.DISPLAY.LANGUAGE_EN'), value: USER_LANGUAGE_SETTINGS.en },
        ],
        [t],
    );

    const statusColorModeOptions = useMemo(
        () => [
            {
                label: t('TOP_HEADER.USER_SETTINGS.SETTINGS.DISPLAY.STATUS_COLOR_MODE_SYSTEM'),
                value: USER_STATUS_COLOR_MODES.SYSTEM,
            },
            {
                label: t('TOP_HEADER.USER_SETTINGS.SETTINGS.DISPLAY.STATUS_COLOR_MODE_COLORFUL'),
                value: USER_STATUS_COLOR_MODES.COLORFUL,
            },
        ],
        [t],
    );

    return (
        <SectionCard title={t('TOP_HEADER.USER_SETTINGS.SETTINGS.DISPLAY.TITLE')} outlined>
            <div className="flex flex-col gap-2">
                <FormRow label={t('TOP_HEADER.USER_SETTINGS.SETTINGS.DISPLAY.LANGUAGE')} htmlFor="language">
                    <div className="flex min-w-0 grow">
                        <DropdownMenu
                            id="language"
                            options={languageOptions}
                            value={languageField.value}
                            onChange={(value) => languageField.onChange(value)}
                            disabled={disabled}
                            noOptionsText={t('SELECT_NO_OPTIONS')}
                            showBorder
                            triggerClassName="w-full justify-center border-grey-stroke"
                            className="grow"
                        />
                    </div>
                </FormRow>
                <FormRow
                    label={t('TOP_HEADER.USER_SETTINGS.SETTINGS.DISPLAY.STATUS_COLOR_MODE')}
                    htmlFor="statusColorMode"
                >
                    <div className="flex min-w-0 grow">
                        <DropdownMenu
                            id="statusColorMode"
                            options={statusColorModeOptions}
                            value={statusColorModeField.value}
                            onChange={(value) => statusColorModeField.onChange(value)}
                            disabled={disabled}
                            noOptionsText={t('SELECT_NO_OPTIONS')}
                            showBorder
                            triggerClassName="w-full justify-center border-grey-stroke"
                            className="grow"
                        />
                    </div>
                </FormRow>
            </div>
        </SectionCard>
    );
};
